// rebuildPipeline — пересчёт всех тел из doc.features.
//
// При любом изменении (редактирование/удаление/перестановка фичи) мы не пытаемся
// «дописать diff» — мы очищаем все тела в worker'е и проигрываем все features
// заново по порядку. Параметрический CAD так и работает.
//
// Sketch-features ничего не строят (эскизы в store), только фичи геометрии
// (extrude, revolve, combine, shell, moveCopy, fillet, chamfer) идут в worker.
//
// === Проблема runtime-id ===
// combine/fillet/chamfer/shell/moveCopy ссылаются на bodyId (например body_5).
// После clearAll() worker сбрасывает счётчик, и новые тела пойдут body_1, body_2...
// Связь "feature → body" по runtime-id потеряна.
//
// Решение: ведём bodyHistory — массив bodyId в порядке их появления при rebuild.
// При сохранении в features записываем индекс body (а не строковый id), при
// rebuild — берём bodyHistory[index]. На текущий момент это работает только
// для НОВЫХ фич, которые сохраняют резолв через position в массиве.

import useCadStore from './store/useCadStore';
import { getGeometryClient } from './geometryClient';
import { findProfiles, profilePolygonVertices, buildProfilePayloadWithHoles } from './sketch/sketchProfiles';
import { computeParameters, resolveValue } from './parameters';
// Нужен для спасения старых проектов: у них скругления заданы нестабильным
// хешем ребра, и рёбра приходится искать заново по геометрии тела.
import { getEdgeDescriptorsForBody } from './bodyHistoryHelper';
// Итог восстановления показываем пользователю, а не только в консоли.
import { toast } from './ui/Toasts';


function resolveProfile(feature, sketches) {
    const ref = feature.profileRefs?.[0];
    if (!ref) return null;
    const [sketchId, profileId] = ref.split('/');
    const sketch = sketches.find((s) => s.id === sketchId);
    if (!sketch) return null;
    const profiles = findProfiles(sketch);
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return null;

    // Профиль с автоопределением дырок (контур внутри контура → отверстие).
    const profilePayload = buildProfilePayloadWithHoles(profile, profiles, sketch);
    if (!profilePayload) return null;
    return {
        profilePayload,
        planeName:   sketch.planeRef?.plane || 'XY',
        planeOffset: sketch.planeRef?.offset || 0,
        // CUSTOM-плоскость (наклонная грань) — собираем basis из planeRef
        customPlane: (sketch.planeRef?.plane === 'CUSTOM'
            && sketch.planeRef.origin && sketch.planeRef.xDir && sketch.planeRef.normal)
            ? {
                origin: sketch.planeRef.origin,
                xDir:   sketch.planeRef.xDir,
                normal: sketch.planeRef.normal,
            }
            : null,
    };
}


function buildExtrudePayload(feature, sketches) {
    // === Face-extrude (выдавливание грани существующего тела) ===
    if (feature.faceProfile) {
        const fp = feature.faceProfile;
        const baseSign = fp.sign || 1;
        return {
            /* holes — внутренние контуры кольцевой грани. Их надо передавать
               и при ПЕРЕСЧЁТЕ, иначе после перезагрузки документа деталь
               перестроится сплошной и операция вырезания снова упадёт. */
            profile: {
                kind: 'polygon',
                verts: fp.verts,
                ...(fp.holes ? { holes: fp.holes } : {}),
            },
            planeName: fp.planeName,
            planeOffset: fp.planeOffset,
            customPlane: fp.customPlane || null,
            distance: (feature.distance || 0) * baseSign,
            direction: feature.direction,
            operation: feature.operation,
        };
    }
    // === Sketch-extrude (классический выдавливание профиля эскиза) ===
    const r = resolveProfile(feature, sketches);
    if (!r) return null;
    return {
        profile: r.profilePayload,
        planeName: r.planeName,
        planeOffset: r.planeOffset,
        customPlane: r.customPlane,
        distance: feature.distance,
        direction: feature.direction,
        operation: feature.operation,
    };
}


const REBUILD_AXIS_VECTORS = {
    sketch_X: [1, 0, 0],
    sketch_Y: [0, 1, 0],
};

function buildRevolvePayload(feature, sketches) {
    const r = resolveProfile(feature, sketches);
    if (!r) return null;
    const axisVec = REBUILD_AXIS_VECTORS[feature.axis] || REBUILD_AXIS_VECTORS.sketch_Y;
    return {
        profile: r.profilePayload,
        planeName: r.planeName,
        planeOffset: r.planeOffset,
        customPlane: r.customPlane,   // поддержка наклонной грани (как в extrude)
        axis: axisVec,
        angle: feature.angle,
        operation: feature.operation,
    };
}


// Плоскость эскиза → формат воркера (как sketchPlaneForWorker в Properties).
function sketchPlaneRefForWorker(sketch) {
    const pr = sketch?.planeRef || {};
    if (pr.plane === 'CUSTOM' && pr.origin && pr.xDir && pr.normal) {
        return { planeName: 'CUSTOM', customPlane: { origin: pr.origin, xDir: pr.xDir, normal: pr.normal } };
    }
    return { planeName: pr.plane || 'XY', planeOffset: pr.offset || 0 };
}

// Контур профиля (с дырками) по ref 'sketchId/profileId'.
function contourByRef(ref, sketches) {
    if (!ref) return null;
    const [sketchId, profileId] = ref.split('/');
    const sketch = sketches.find((s) => s.id === sketchId);
    if (!sketch) return null;
    const profiles = findProfiles(sketch);
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return null;
    const payload = buildProfilePayloadWithHoles(profile, profiles, sketch);
    return payload ? { contour: payload, sketch, profile } : null;
}

function buildSweepPayload(feature, sketches) {
    const prof = contourByRef(feature.profileRef, sketches);
    const pathP = contourByRef(feature.pathRef, sketches);
    if (!prof || !pathP) return null;
    const pathVerts = profilePolygonVertices(pathP.profile, pathP.sketch);
    if (!pathVerts || pathVerts.length < 2) return null;
    return {
        profile: prof.contour,
        profilePlane: sketchPlaneRefForWorker(prof.sketch),
        path: { kind: 'polygon', verts: pathVerts },
        pathPlane: sketchPlaneRefForWorker(pathP.sketch),
        operation: feature.operation || 'new',
    };
}

function buildLoftPayload(feature, sketches) {
    const refs = feature.profileRefs || [];
    if (refs.length < 2) return null;
    const profiles = [];
    for (const ref of refs) {
        const p = contourByRef(ref, sketches);
        if (!p) return null;
        profiles.push({ contour: p.contour, plane: sketchPlaneRefForWorker(p.sketch) });
    }
    return { profiles, operation: feature.operation || 'new' };
}


/**
 * Полный пересчёт всех тел документа.
 * Вызывается после edit/remove/move-feature или после loadDocumentFromJson.
 */
export async function rebuildAllBodies() {
    const state = useCadStore.getState();
    const features = state.doc.features;
    const sketches = state.doc.sketches;
    const client = getGeometryClient();

    // Вычисляем пользовательские параметры (уравнения). Числовые поля фич
    // (distance/angle/radius/diameter) могут быть строкой-выражением,
    // ссылающейся на параметры — тогда «меняю Width → деталь перестроилась».
    const paramValues = computeParameters(state.doc.parameters || []).values;
    // Резолвит число ИЛИ выражение-строку в число (с fallback на исходное).
    const rv = (field, fallback) => {
        if (typeof field === 'number') return field;
        if (typeof field === 'string') {
            const v = resolveValue(field, paramValues);
            return Number.isFinite(v) ? v : fallback;
        }
        return fallback;
    };

    // Очистить все тела в worker'е (и в store).
    await client.clearAll();

    // bodyHistory — массив всех bodyId в порядке создания. Нужен для
    // resolve runtime-id, на которые ссылаются combine/fillet/chamfer/moveCopy.
    // Каждая создающая фича добавляет 1 id; combine удаляет 2 и добавляет 1.
    const bodyHistory = [];

    // Снимаем snapshot bodyMeshes до фичи — после неё разница даёт нам
    // только что созданный/изменённый bodyId.
    const getCurrentBodyIds = () => {
        const m = useCadStore.getState().bodyMeshes;
        return Array.from(m.keys());
    };

    // Rollback: строим только фичи до маркера (перемотка истории).
    const rollbackIndex = useCadStore.getState().rollbackIndex;
    const buildCount = (typeof rollbackIndex === 'number')
        ? Math.min(rollbackIndex, features.length)
        : features.length;

    // Список операций, которые не удалось построить — покажем человеку.
    const failures = [];

    for (let i = 0; i < buildCount; i++) {
        const feature = features[i];
        // Подавленная фича (suppress) пропускается при пересборке — как в Fusion.
        if (feature.suppressed) continue;
        const before = getCurrentBodyIds();

        try {
            if (feature.kind === 'primitive') {
                await client.createPrimitive({
                    primitiveKind: feature.primitiveKind,
                    width:  rv(feature.width,  feature.width),
                    depth:  rv(feature.depth,  feature.depth),
                    height: rv(feature.height, feature.height),
                    radius: rv(feature.radius, feature.radius),
                    tube:   rv(feature.tube,   feature.tube),
                    position: feature.position || [0, 0, 0],
                });
            } else if (feature.kind === 'extrude') {
                const payload = buildExtrudePayload(feature, sketches);
                if (!payload) {
                    console.warn('[rebuild] нет payload для extrude', feature.id);
                    continue;
                }
                // distance может быть выражением-параметром → вычисляем.
                payload.distance = rv(feature.distance, payload.distance);
                // Целевое тело для add/cut — по сохранённому индексу истории.
                if (typeof feature.targetBodyIdx === 'number') {
                    const tid = bodyHistory[feature.targetBodyIdx];
                    if (tid) payload.targetBodyId = tid;
                }
                await client.extrudeSketchProfile(payload);
            } else if (feature.kind === 'revolve') {
                const payload = buildRevolvePayload(feature, sketches);
                if (!payload) {
                    console.warn('[rebuild] нет payload для revolve', feature.id);
                    continue;
                }
                payload.angle = rv(feature.angle, payload.angle);
                await client.revolveSketchProfile(payload);
            } else if (feature.kind === 'sweep') {
                const p = buildSweepPayload(feature, sketches);
                if (!p) { console.warn('[rebuild] нет payload для sweep', feature.id); continue; }
                await client.sweepProfile(p);
            } else if (feature.kind === 'loft') {
                const p = buildLoftPayload(feature, sketches);
                if (!p) { console.warn('[rebuild] нет payload для loft', feature.id); continue; }
                await client.loftProfiles(p);
            } else if (feature.kind === 'combine') {
                // Combine может быть в двух форматах:
                //   1. {targetIdx, toolIdx} — обычный combine на 2 тела
                //   2. {sourceIdxs: [i1, i2, i3, ...]} — chain-combine на N тел
                //      (из Browser.jsx, последовательно объединяет все источники)
                const operation = feature.operation || 'add';
                const keepTool = !!feature.keepTool;

                let idxs;
                if (Array.isArray(feature.sourceIdxs) && feature.sourceIdxs.length >= 2) {
                    idxs = feature.sourceIdxs;
                } else if (typeof feature.targetIdx === 'number' && typeof feature.toolIdx === 'number') {
                    idxs = [feature.targetIdx, feature.toolIdx];
                } else {
                    // Старый формат — fallback на последние 2 body
                    if (bodyHistory.length < 2) {
                        console.warn('[rebuild] combine: меньше 2 тел в истории', feature.id);
                        continue;
                    }
                    idxs = [bodyHistory.length - 2, bodyHistory.length - 1];
                }

                // Резолвим в текущие bodyId. ВАЖНО: после первого combineBodies
                // target+tool удаляются из bodyHistory, поэтому первое выполнение
                // нужно сделать ДО обновления истории. Внутри for body будут
                // последовательно удаляться/добавляться, и мы перевычисляем.
                let currentTarget = bodyHistory[idxs[0]];
                if (!currentTarget) {
                    console.warn('[rebuild] combine: target по idx не найден', feature.id, idxs[0]);
                    continue;
                }
                let chainOk = true;
                for (let k = 1; k < idxs.length; k++) {
                    const toolId = bodyHistory[idxs[k]];
                    if (!toolId) {
                        console.warn('[rebuild] combine: tool по idx не найден', feature.id, idxs[k]);
                        chainOk = false;
                        break;
                    }
                    const beforeIter = getCurrentBodyIds();
                    await client.combineBodies({
                        targetId: currentTarget, toolId, operation, keepTool,
                    });
                    const afterIter = getCurrentBodyIds();
                    // Подтягиваем bodyHistory: удаляем target+tool, добавляем новый
                    const removedIter = beforeIter.filter((id) => !afterIter.includes(id));
                    const addedIter = afterIter.filter((id) => !beforeIter.includes(id));
                    for (const r of removedIter) {
                        const ridx = bodyHistory.indexOf(r);
                        if (ridx >= 0) bodyHistory.splice(ridx, 1);
                    }
                    for (const a of addedIter) bodyHistory.push(a);
                    currentTarget = addedIter[0] || currentTarget;
                }
                if (!chainOk) continue;
                // Для chain-combine after-обновление bodyHistory мы уже делали
                // внутри цикла — общий after-блок не должен дублировать. Помечаем
                // флагом чтобы пропустить общую логику ниже.
                feature.__rebuildSkipDiff = true;
            } else if (feature.kind === 'fillet' || feature.kind === 'chamfer') {
                // Двойной механизм поиска рёбер:
                //   (a) edgeRefs ['bodyId/hashCode',...] — старый способ, hashCode
                //       стабилен только если pipeline идентичен. После rebuild часто
                //       сбивается.
                //   (b) edgeGeoms [{a:[x,y,z], b:[x,y,z]}, ...] — геометрия концов
                //       ребра. Worker matchesEndpoints находит ребро по координатам.
                // Достаточно ОДНОГО из них, чтобы fillet попытался применить себя.
                // BUG-FIX: раньше при пустом edgeRefs фича пропускалась полностью,
                // даже если edgeGeoms был валидным. Это ломало демо-проекты и
                // потерю скруглений при сохранении/загрузке.
                const refs = feature.edgeRefs || [];
                const geoms = feature.edgeGeoms || [];
                const descs = feature.edgeDescs || [];
                if (refs.length === 0 && geoms.length === 0 && descs.length === 0) {
                    console.warn('[rebuild] fillet/chamfer без edges',
                        feature.id, '— пропуск');
                    continue;
                }

                // Парсим edgeIds (hashCodes) — могут не пригодиться, если refs пуст
                const edgeIds = [];
                if (refs.length > 0) {
                    for (const ref of refs) {
                        const parts = ref.split('/');
                        const eid = parseInt(parts[1], 10);
                        if (!Number.isNaN(eid)) edgeIds.push(eid);
                    }
                }

                // Резолв body: targetBodyIdx → последнее body
                let curBodyId;
                if (typeof feature.targetBodyIdx === 'number') {
                    curBodyId = bodyHistory[feature.targetBodyIdx];
                } else {
                    curBodyId = bodyHistory[bodyHistory.length - 1];
                }
                if (!curBodyId) {
                    console.warn('[rebuild] fillet/chamfer без body', feature.id);
                    continue;
                }

                /*
                 * СПАСЕНИЕ СТАРЫХ ПРОЕКТОВ (до 2026-07-24).
                 *
                 * Тогда скругление запоминалось ТОЛЬКО хешем ребра
                 * (edgeRefs = 'body_2/570673729'). Хеш живёт лишь пока pipeline
                 * идентичен: при следующем открытии номера рёбер другие, ребро
                 * не находится, и скругление молча пропадает. А следом
                 * разъезжается всё, что построено после него.
                 *
                 * Здесь пробуем найти те же рёбра ГЕОМЕТРИЧЕСКИ. Единственное,
                 * что известно достоверно, — сколько их было (длина edgeRefs).
                 * Берём самые длинные вертикальные рёбра тела: именно их
                 * скругляют в 99% случаев (боковые углы корпуса), и берём
                 * ровно столько, сколько было записано. Если подходящих
                 * оказалось меньше — не выдумываем, отдаём что нашли и
                 * помечаем фичу как восстановленную приблизительно.
                 */
                let restored = null;
                if (descs.length === 0 && geoms.length === 0 && edgeIds.length > 0) {
                    const all = getEdgeDescriptorsForBody(curBodyId);
                    if (all.length > 0) {
                        // Вертикальное ребро: концы различаются почти только по Z.
                        // mid+len у нас есть, поэтому ориентацию берём из меша
                        // косвенно — через отбор самых длинных рёбер, они и есть
                        // рёбра высоты у коробчатых деталей.
                        const maxLen = Math.max(...all.map((e) => e.len));
                        const want = edgeIds.length;
                        restored = all
                            .filter((e) => Math.abs(e.len - maxLen) < Math.max(0.05, maxLen * 0.02))
                            .sort((a, b) => (a.mid[0] - b.mid[0]) || (a.mid[1] - b.mid[1]))
                            .slice(0, want);
                        if (restored.length > 0) {
                            console.warn('[rebuild] fillet', feature.id,
                                `— старый формат: рёбра восстановлены по геометрии (${restored.length}/${want})`);
                            // Записываем в фичу: после первого же сохранения
                            // проект станет устойчивым и чинить будет нечего.
                            feature.edgeDescs = restored.map((e) => ({ mid: e.mid, len: e.len }));
                            feature.__restoredEdges = true;
                            if (restored.length < want) feature.__restoredPartial = true;
                        } else {
                            feature.__edgesLost = true;
                        }
                    }
                }

                await client.filletOrChamfer({
                    bodyId: curBodyId,
                    edgeIds,
                    edgeDescs: restored
                        ? restored.map((e) => ({ mid: e.mid, len: e.len }))
                        : descs,            // основной механизм (mid+len)
                    edgeEndpoints: geoms,   // fallback для старых проектов
                    radius: rv(feature.radius, feature.radius),
                    kind: feature.kind,
                    preview: false,
                });
            } else if (feature.kind === 'scale') {
                // Масштаб заменяет тело новым — цель берём по индексу истории.
                const idx = (typeof feature.targetBodyIdx === 'number')
                    ? feature.targetBodyIdx : bodyHistory.length - 1;
                const curBodyId = bodyHistory[idx];
                if (!curBodyId) {
                    console.warn('[rebuild] scale без тела', feature.id);
                    continue;
                }
                await client.scaleBody(curBodyId, rv(feature.factor, feature.factor));
            } else if (feature.kind === 'coil') {
                // Спираль — самостоятельное тело, цели не требует.
                await client.createCoil({
                    radius: rv(feature.radius, feature.radius),
                    pitch: rv(feature.pitch, feature.pitch),
                    height: rv(feature.height, feature.height),
                    wireDiameter: rv(feature.wireDiameter, feature.wireDiameter),
                    section: feature.section || 'circle',
                    lefthand: !!feature.lefthand,
                });
            } else if (feature.kind === 'text') {
                /*
                 * Объёмный текст. Без этой ветки надпись исчезала при любой
                 * правке истории: rebuild проигрывает шаги заново, а про 'text'
                 * не знал — шаг молча пропускался.
                 *
                 * Размер букв и высоту резолвим через rv(): их можно задать
                 * пользовательским параметром (например, ширина брелка), и
                 * тогда надпись перестроится вместе с деталью.
                 */
                const payload = {
                    text: feature.text,
                    fontSize: rv(feature.fontSize, feature.fontSize || 10),
                    thickness: rv(feature.thickness, feature.thickness || 2),
                    operation: feature.operation || 'new',
                };
                if (feature.customPlane) payload.customPlane = feature.customPlane;
                else {
                    payload.planeName = feature.planeName || 'XY';
                    payload.planeOffset = feature.planeOffset || 0;
                }
                if (payload.operation !== 'new') {
                    const idx = (typeof feature.targetBodyIdx === 'number')
                        ? feature.targetBodyIdx
                        : bodyHistory.length - 1;
                    const curBodyId = bodyHistory[idx];
                    if (!curBodyId) {
                        console.warn('[rebuild] text без тела-цели', feature.id);
                        continue;
                    }
                    payload.targetBodyId = curBodyId;
                }
                await client.createText(payload);
            } else if (feature.kind === 'hole') {
                // Отверстие — вырез цилиндром через грань. Ссылается на
                // тело по targetBodyIdx (как fillet/shell). planeRef и
                // faceCenter сохранены в фиче на момент создания.
                const targetBodyIdx = (typeof feature.targetBodyIdx === 'number')
                    ? feature.targetBodyIdx
                    : bodyHistory.length - 1;
                const curBodyId = bodyHistory[targetBodyIdx];
                if (!curBodyId) {
                    console.warn('[rebuild] hole без body', feature.id);
                    continue;
                }
                await client.createHole({
                    targetBodyId: curBodyId,
                    planeRef: feature.planeRef,
                    faceCenter: feature.faceCenter,
                    offsetX: rv(feature.offsetX, feature.offsetX || 0),
                    offsetY: rv(feature.offsetY, feature.offsetY || 0),
                    diameter: rv(feature.diameter, feature.diameter),
                    depth: rv(feature.depth, feature.depth),
                    depthType: feature.depthType || 'blind',
                });
            } else if (feature.kind === 'mirror') {
                // Зеркало — добавляет новое тело (зеркальную копию),
                // оригинал остаётся. Ссылается на исходное тело по
                // sourceBodyIdx.
                const sourceBodyIdx = (typeof feature.sourceBodyIdx === 'number')
                    ? feature.sourceBodyIdx
                    : bodyHistory.length - 1;
                const curBodyId = bodyHistory[sourceBodyIdx];
                if (!curBodyId) {
                    console.warn('[rebuild] mirror без body', feature.id);
                    continue;
                }
                await client.mirrorBody({
                    sourceBodyId: curBodyId,
                    plane: feature.plane || 'YZ',
                });
            } else if (feature.kind === 'rectPattern') {
                // Прямоугольный массив — добавляет сетку копий тела.
                // Ссылается на исходное тело по sourceBodyIdx.
                const sourceBodyIdx = (typeof feature.sourceBodyIdx === 'number')
                    ? feature.sourceBodyIdx
                    : bodyHistory.length - 1;
                const curBodyId = bodyHistory[sourceBodyIdx];
                if (!curBodyId) {
                    console.warn('[rebuild] rectPattern без body', feature.id);
                    continue;
                }
                await client.rectPattern({
                    sourceBodyId: curBodyId,
                    countX: feature.countX || 1,
                    countY: feature.countY || 1,
                    stepX: feature.stepX || 0,
                    stepY: feature.stepY || 0,
                });
            } else if (feature.kind === 'circPattern') {
                // Круговой массив — копии тела по окружности вокруг оси.
                const sourceBodyIdx = (typeof feature.sourceBodyIdx === 'number')
                    ? feature.sourceBodyIdx
                    : bodyHistory.length - 1;
                const curBodyId = bodyHistory[sourceBodyIdx];
                if (!curBodyId) {
                    console.warn('[rebuild] circPattern без body', feature.id);
                    continue;
                }
                await client.circPattern({
                    sourceBodyId: curBodyId,
                    axis: feature.axis || 'Z',
                    axisOrigin: feature.axisOrigin,
                    axisDir: feature.axisDir,
                    count: feature.count || 1,
                    angle: feature.angle != null ? feature.angle : 360,
                });
            } else if (feature.kind === 'shell') {
                const fcs = feature.faces || [];
                if (fcs.length === 0) continue;
                // Берём первое body — все faces должны быть из одного
                const targetBodyIdx = (typeof feature.targetBodyIdx === 'number')
                    ? feature.targetBodyIdx
                    : bodyHistory.length - 1;
                const curBodyId = bodyHistory[targetBodyIdx];
                if (!curBodyId) {
                    console.warn('[rebuild] shell без body', feature.id);
                    continue;
                }
                await client.shellBody({
                    bodyId: curBodyId,
                    thickness: feature.thickness,
                    faceCenters: fcs.map((f) => f.center),
                    preview: false,
                });
            } else if (feature.kind === 'moveCopy') {
                const targetBodyIdx = (typeof feature.targetBodyIdx === 'number')
                    ? feature.targetBodyIdx
                    : bodyHistory.length - 1;
                const curBodyId = bodyHistory[targetBodyIdx];
                if (!curBodyId) {
                    console.warn('[rebuild] moveCopy без body', feature.id);
                    continue;
                }
                await client.moveOrCopyBody({
                    bodyId: curBodyId,
                    dx: feature.dx || 0,
                    dy: feature.dy || 0,
                    dz: feature.dz || 0,
                    rx: feature.rx || 0,
                    ry: feature.ry || 0,
                    rz: feature.rz || 0,
                    copy: !!feature.copy,
                    preview: false,
                });
            }
            // sketch-features ничего не строят
        } catch (e) {
            /*
             * 🔴 Операция не построилась — говорим об этом ЧЕЛОВЕКУ.
             *
             * Раньше ошибка уходила только в консоль, и операция молча
             * пропадала: скругление исчезало с модели, а его строка в
             * хронологии оставалась. Человек видел острый угол вместо
             * скруглённого и не понимал, что произошло.
             *
             * Так делает Fusion: сломанная операция помечается в хронологии
             * и объясняет причину, а не притворяется выполненной.
             */
            console.error(`[rebuild] ${feature.kind} failed:`, feature.id, e);
            feature.__error = (e && e.message) ? String(e.message) : String(e);
            failures.push({ id: feature.id, kind: feature.kind, message: feature.__error });
            continue;
        }
        // Построилось — снимаем прежнюю пометку об ошибке.
        if (feature.__error) delete feature.__error;

        // Chain-combine уже обновил bodyHistory внутри цикла — не дублируем
        if (feature.__rebuildSkipDiff) {
            delete feature.__rebuildSkipDiff;
            continue;
        }

        // Обновляем bodyHistory: смотрим какие body появились/изменились/исчезли.
        // primitive/extrude(new)/revolve(new) — добавляют 1 новое body
        // extrude(add/cut/intersect) — удаляют последний target и добавляют новый
        // combine — удаляют target+tool, добавляют 1 новый
        // fillet/chamfer/shell — заменяют 1 body (тот же bodyId или новый)
        // moveCopy(copy=false) — заменяет тот же bodyId
        // moveCopy(copy=true) — добавляет новый bodyId
        const after = getCurrentBodyIds();
        const added = after.filter((id) => !before.includes(id));
        const removed = before.filter((id) => !after.includes(id));
        for (const r of removed) {
            const idx = bodyHistory.indexOf(r);
            if (idx >= 0) bodyHistory.splice(idx, 1);
        }
        for (const a of added) {
            bodyHistory.push(a);
        }
    }

    /*
     * Итог по старым проектам — ОДНИМ сообщением, а не по каждому шагу.
     *
     * Раньше потеря рёбер видна была только в консоли: модель молча
     * открывалась «недостроенной», и человек думал, что потерял работу.
     */
    const restoredCnt = features.filter((f) => f.__restoredEdges).length;
    const partialCnt  = features.filter((f) => f.__restoredPartial).length;
    const lostCnt     = features.filter((f) => f.__edgesLost).length;

    if (restoredCnt > 0) {
        const word = restoredCnt === 1 ? 'скругление' : 'скругления';
        toast(
            `Проект сохранён в старом формате: ${restoredCnt} ${word} восстановлено ` +
            `по геометрии${partialCnt ? ' (частично)' : ''}. Сохраните проект — ` +
            'дальше он будет открываться без потерь.',
            partialCnt ? 'warning' : 'info',
        );
    }
    if (lostCnt > 0) {
        const word = lostCnt === 1 ? 'скругления' : 'скруглений';
        toast(
            `Не удалось восстановить рёбра для ${lostCnt} ${word} — они сохранены ` +
            'в устаревшем формате. Откройте операцию в хронологии и выберите рёбра заново.',
            'error',
        );
    }
    /*
     * Механические соединения применяем ПОСЛЕ всех операций.
     *
     * Соединение не строит геометрию — оно ставит уже готовое тело в
     * заданное положение. Применять его раньше бессмысленно: тела, которое
     * надо повернуть, ещё не существует.
     *
     * Без этого шага положение механизма терялось при любом пересчёте:
     * соединение оставалось в документе, а детали разъезжались по исходным
     * местам.
     */
    for (const jt of (state.doc.joints || [])) {
        if (!jt || jt.kind === 'rigid') continue;
        if (!jt.bodyB) continue;
        /*
         * Тело ищем по идентификатору из соединения. Он переживает
         * пересчёт: тела с теми же операциями получают те же имена.
         */
        const bodyId = jt.bodyB;
        try {
            await client.applyJoint({
                bodyId,
                kind: jt.kind,
                value: jt.value || 0,
                origin: jt.origin || [0, 0, 0],
                axis: jt.axis || [0, 0, 1],
            });
        } catch (e) {
            // Тело могло исчезнуть — соединение просто не применяем,
            // но модель не роняем: остальные детали построены верно.
            console.warn('[rebuild] соединение не применилось:', jt.id, e?.message || e);
        }
    }

    /*
     * Операции, которые не построились. Показываем ПЕРВУЮ причину целиком:
     * список из пяти сообщений человек не прочитает, а первая сломавшаяся
     * операция обычно и есть корень — остальные валятся следом за ней.
     */
    if (failures.length > 0) {
        const first = failures[0];
        const more = failures.length > 1
            ? ` И ещё ${failures.length - 1} операц${failures.length === 2 ? 'ия' : 'ий'} следом.`
            : '';
        toast(first.message + more, 'error');
    }

    features.forEach((f) => {
        delete f.__restoredEdges;
        delete f.__restoredPartial;
        delete f.__edgesLost;
    });
}
